import { Field, Form, Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import * as yup from 'yup'
import info from '../../assets/icons/info2.svg'
import Button from '../../components/button'
import FileInput from '../../components/fileInput'
import H3 from '../../components/h3'
import InformationPopup from '../../components/informationPopup'
import Input from '../../components/input'
import InputRadio from '../../components/inputRadio'
import Modal from '../../components/modal'
import SelectInput from '../../components/selectInput'
import { useLabs } from '../../hooks/labs'
import { usePatients } from '../../hooks/patients'
import { ProcessPdf, useProcessExam } from '../../hooks/processExam'
import { RoutesEnum } from '../../utils/routesEnum'
import ModalImcCalculator from '../imcCalculator/modal'
import TabItems from '../loggedArea/tabItems'
import { Container, Info, InfoContainer, SmallLink } from './style'
import TypeExams, { TypeExamsValues } from './type'

type ProcessExamProps = {
  type: 'upload' | 'type'
}
const ProcessExam: FC<ProcessExamProps> = ({ type }): ReactElement => {
  const history = useHistory()
  const { parameters, processPdf, applyRules } = useProcessExam()
  const [warnModal, setOpenWarnModal] = useState(false)
  const { labs, get: getLabs } = useLabs()
  const { patientsList, getList } = usePatients()
  const [examsTyped, setExamsTyped] = useState<TypeExamsValues[]>([])

  useEffect(() => {
    getList()
  }, [getList])

  useEffect(() => {
    if (isEmpty(labs)) {
      getLabs()
    }
  }, [labs, getLabs])

  const activeItem = useCallback(() => {
    if (history.location.pathname === RoutesEnum.PROCESS_EXAMS_TYPE) return 1
    if (history.location.pathname === RoutesEnum.PROCESS_EXAMS_REQUEST_LAB) return 2

    return 0
  }, [history])

  const onTabChange = useCallback((index: number) => {
    if (index === 0) history.push(RoutesEnum.PROCESS_EXAMS)
    if (index === 1) setOpenWarnModal(true)
    if (index === 2) history.push(RoutesEnum.PROCESS_EXAMS_REQUEST_LAB)
  }, [history, setOpenWarnModal])

  const labsOption = useMemo(() => labs.map(lab => ({
    value: lab._id,
    label: lab.name,
    image: lab.image
  })), [labs])

  const patientsOption = useMemo(() => patientsList.map(patient => ({
    label: patient.name,
    value: patient._id
  })), [patientsList])

  const schema = useMemo(() => {
    if (type === 'type') {
      return yup.object().shape({
        processType: yup.string().required('Item obrigatório'),
        age: yup.number().when('processType', {
          is: 'empty',
          then: yup.number().typeError('Idade Inválida').required('Idade obrigatória'),
          otherwise: yup.number().typeError('Idade Inválida').nullable()
        }),
        imc: yup.number().when('processType', {
          is: 'empty',
          then: yup.number().typeError('IMC inválido').required('IMC obrigatório'),
          otherwise: yup.number().typeError('IMC inválido').nullable()
        }),
        gender: yup.string().when('processType', {
          is: 'empty',
          then: yup.string().required('Sexo obrigatório'),
          otherwise: yup.string().nullable()
        }),
        patientId: yup.string().when('processType', {
          is: 'user-selected',
          then: yup.string().required('Paciente obrigatório'),
          otherwise: yup.string().nullable()
        }),
        pregnant: yup.string().when('gender', {
          is: 'female',
          then: yup.string().required('Item obrigatório'),
          otherwise: yup.string().nullable()
        }),
        hasMenopause: yup.string().when('gender', {
          is: 'female',
          then: yup.string().required('Item obrigatório'),
          otherwise: yup.string().nullable()
        })
      })
    }
    return yup.object().shape({
      labId: yup.string().required('Laboratório obrigatório'),
      processType: yup.string().required('Item obrigatório'),
      age: yup.number().when('processType', {
        is: 'empty',
        then: yup.number().typeError('Idade Inválida').required('Idade obrigatória'),
        otherwise: yup.number().typeError('Idade Inválida').nullable()
      }),
      imc: yup.number().when('processType', {
        is: 'empty',
        then: yup.number().typeError('IMC inválido').required('IMC obrigatório'),
        otherwise: yup.number().typeError('IMC inválido').nullable()
      }),
      gender: yup.string().when('processType', {
        is: 'empty',
        then: yup.string().required('Sexo obrigatório'),
        otherwise: yup.string().nullable()
      }),
      pregnant: yup.string().when('gender', {
        is: 'female',
        then: yup.string().required('Item obrigatório'),
        otherwise: yup.string().nullable()
      }),
      hasMenopause: yup.string().when('gender', {
        is: 'female',
        then: yup.string().required('Item obrigatório'),
        otherwise: yup.string().nullable()
      }),
      patientId: yup.string().when('processType', {
        is: 'user-selected',
        then: yup.string().required('Paciente obrigatório'),
        otherwise: yup.string().nullable()
      }),
      files: yup.array().min(1, 'Selecione ao menos um arquivo').required('Selecione ao menos um arquivo')
    })
  }, [type])

  const submit = async (values: Partial<ProcessPdf>, helpers: FormikHelpers<Partial<ProcessPdf>>): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      const patient = patientsList.find(({ _id }) => _id === values.patientId)

      let key = null
      if (type === 'type') {
        key = await applyRules({
          ...values,
          gender: String(patient?.sex || values.gender),
          age: String(patient?.age || values.age),
          imc: String(patient?.imc || values.imc),
          exams: examsTyped.map(e => ({
            examId: e.examId || '',
            result: Number(e.result || 0),
            measurement: e.unit || ''
          }))
        })
      } else {
        key = await processPdf({
          ...values,
          gender: String(patient?.sex || values.gender),
          age: String(patient?.age || values.age),
          imc: String(patient?.imc || values.imc),
          files: values.files || [],
          labCode: labs.find(lab => lab._id === values.labId)?.code || ''
        })
      }

      history.push(`${RoutesEnum.PROCESSED_EXAMS.replace(':extractionId', key)}${values.patientId ? '?patientId=' + values.patientId : ''}`)
    } catch { }
    helpers.setSubmitting(false)
  }

  return (
    <TabItems
      activeItem={activeItem()}
      onChangeTab={onTabChange}
      title='Inserir Exame'
      breadcrumbs={[
        { title: 'Processar Exames', to: RoutesEnum.PROCESS_EXAMS },
        { title: 'Inserir Exame', to: RoutesEnum.PROCESS_EXAMS },
        ...(activeItem() === 1 ? [{ title: 'Digitar Exame', to: RoutesEnum.PROCESS_EXAMS_TYPE }] : [])
      ]}
      subtitle={<span>Preencha os dados do laboratório e paciente <br /> para o processamento das regras do exame.</span>}
      info='Caso você não tenha conseguido ler o PDF do exame, você pode pedir a homologação dele ou digitar manualmente'
    >
      <Container>
        <H3>
          Dados do laboratório e paciente
          <InformationPopup
            content={`Preencha todos os dados de maneira correta,
            se tiver algum problema, digite manualmente ou solicite ao
            suporte a adição do laboratório que você quer usar.`}
          />
        </H3>
        <Formik
          validationSchema={schema}
          initialValues={{
            processType: 'empty',
            ...parameters
          }}
          onSubmit={submit}
        >
          {({ setFieldValue, values, isSubmitting, isValid }) => (
            <Form>
              <Field
                rightAttachment={<SmallLink to={RoutesEnum.PATIENTS_CREATE}>Cadastrar paciente</SmallLink>}
                label='Processamento de exame:'
                name='processType'
                options={[
                  { name: 'Avulso', value: 'empty' },
                  { name: 'Paciente Cadastrado', value: 'user-selected' }
                ]}
                component={InputRadio}
              />
              {type === 'upload' && (
                <Field
                  label='Nome do laboratório'
                  name='labId'
                  options={labsOption}
                  component={SelectInput}
                />
              )}
              {values.processType === 'empty' && (
                <>
                  <Field
                    label='Idade do paciente'
                    name='age'
                    type='tel'
                    maxLength={3}
                    component={Input}
                  />
                  <Field
                    label='IMC do paciente'
                    name='imc'
                    type='tel'
                    maxLength={3}
                    component={Input}
                    info={<Link to='?imcCalc=true'>Calculadora IMC</Link>}
                  />
                  <Field
                    label='Sexo do paciente'
                    name='gender'
                    options={[
                      { name: 'Masculino', value: 'male' },
                      { name: 'Feminino', value: 'female' }
                    ]}
                    component={InputRadio}
                  />
                  {values.gender === 'female' && (
                    <>
                      <Field
                        label='Gravidez'
                        name='pregnant'
                        options={[
                          { name: 'Grávida', value: 'pregnant' },
                          { name: 'Não grávida', value: 'not-pregnant' }
                        ]}
                        component={InputRadio}
                      />
                      <Field
                        label='Menopausa'
                        name='hasMenopause'
                        options={[
                          { name: 'Menopausa', value: 'menopause' },
                          { name: 'Não menopausa', value: 'not-menopause' }
                        ]}
                        component={InputRadio}
                      />
                    </>
                  )}
                </>
              )}
              {values.processType === 'user-selected' && (
                <Field
                  label='Paciente'
                  name='patientId'
                  options={patientsOption}
                  component={SelectInput}
                />
              )}
              {type === 'upload' && (
                <Field
                  label='PDF do Exame'
                  name='files'
                  isMulti
                  component={FileInput}
                />
              )}
              {type === 'type' && (
                <TypeExams
                  onChange={setExamsTyped}
                />
              )}
              <Button
                disabled={isSubmitting || (type === 'type' ? examsTyped.length === 0 || !isValid : !isValid)}
                isLoading={isSubmitting}
                type='submit'
                block
              >
                PROCESSAR EXAME
              </Button>
              <InfoContainer>
                <img alt='info' src={info} />
                <div className='info_content'>
                  O diagnóstico e prescrição de tratamento são atribuições exclusivas
                  do profissional médico, utilize nosso relatório como referência
                  complementar ao seu atendimento e sintomas do paciente.
                </div>
              </InfoContainer>
              <ModalImcCalculator
                setImc={(val) => setFieldValue('imc', val)}
              />
            </Form>
          )}
        </Formik>
        <Info>
          *O CalcLab não tem nenhum patrocínio ou ligação com nenhum dos laboratórios listados no
          sistema. Todos os direitos de marca são de propriedade de suas respectivas empresas.
        </Info>
      </Container>
      {warnModal && (
        <Modal
          title='Digitar Exame'
          okButtonText='CONTINUAR MESMO ASSIM'
          onOk={() => {
            history.push(RoutesEnum.PROCESS_EXAMS_TYPE)
            setOpenWarnModal(false)
          }}
          onClose={() => setOpenWarnModal(false)}
        >
          Digitar o exame não é a melhor experiência que o CalcLab pode
          oferecer, o ideal é que caso seu laboratório não esteja em
          nossa lista, você utilize o botão (Solicitar novo laboratório)
          para que possamos incluí-lo no sistema.
        </Modal>
      )}
    </TabItems>
  )
}

export default ProcessExam
